import noop from "lodash/noop";
import * as React from "react";
import { useEffect, useState, useContext } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { ArrayParam, StringParam, useQueryParams } from "use-query-params";
import { endpoints } from "../../endpoints";
import { FiltersWrapper } from "../../layout/publicPageLayout";
import { applyFiltersToURL } from "../../util/filterStateManager";
import { loadFilters } from "../../util/filterStateManager";
import { screenSize } from "../../theme";
import type {
  FilterGroup,
  Filters,
  IFilters,
  IPresetDataItem,
  IPresetFilter,
  NumericFilters,
  ProductFiltersMainSchema,
  SupportedLanguage,
  TagClassificationConfig,
  WithPagination,
} from "../../types/types";
import type {
  Assets,
  AttributeTemplateSummarySchema,
  ProductStatusType,
  ProductStatusesSchema,
} from "../../types/types.PIM";
import {
  convertToChipArray,
  getCustomLabel,
  makeUrlWithParams,
  useStoreState,
} from "../../util/util";
import { useAuthContext } from "../Auth";
import { TertiaryTextButtonSmall } from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { FilterBy } from "../FilterBy/FilterBy";
import { LoadingIcon } from "../Icons/Icons";
import { getProductStatusText, getProductStatusValue } from "../Status/Status";
import { SoftHeaderRegular } from "../Typography/Typography";
import { DocumentFilterTag } from "./DocumentFilterTag";
import { SearchFilterTag } from "./SearchFilterTag";
import { PresetFilter } from "./PresetFilter";
import { NumericFilterButton } from "./NumericFilterInput";
import type { AxiosError } from "axios";
import axios from "axios";
import { Notifications } from "../Notifications/NotificationsContext";
import { EntityType } from "../../types/types.MDM";
import {
  chipsToStringArray,
  decodeNumericFilterCriterion,
} from "./filterutils";

const EmptyFiltersPlaceholder = styled.div`
  width: 100%;
  height: 30px;
  margin: 0 0 35px;
`;

const SearchFilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin: ${({ theme }) => theme.resolve("spacing_2xl")} 0;
  padding-right: ${({ theme }) => theme.resolve("spacing_md")};
  @media ${screenSize.medium} {
    flex-direction: row;
    margin-bottom: 0;
    overflow-x: scroll;
    gap: ${({ theme }) => theme.resolve("spacing_md")};
  }
`;
const SearchFilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  @media ${screenSize.medium} {
    border-right: ${({ theme }) =>
      `${theme.resolve("border_md")} solid ${theme.primaryBorder}`};
  }
`;

const SearchFilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const FilterNameWrapper = styled(SoftHeaderRegular)`
  border-bottom: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.primaryBorder}`};
  @media ${screenSize.medium} {
    border-bottom: none;
  }
`;

const PresetFilterWrapper = styled.div`
  position: relative;
  margin-bottom: 7px;
`;

const FilterPortalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 3;
`;

const FilterByWrapper = styled.div`
  position: relative;
  margin-right: 0;
  margin-bottom: 0;
`;

type FilterAreaProps = {
  resetOffset?: React.Dispatch<React.SetStateAction<number>>;
  clearFilter: () => void;
  customLabels: TagClassificationConfig[];
  showStatusFilter?: boolean;
  document_and_ai_search_filter?: boolean;
  exit_document_and_ai_search_filter?: () => void;
  viewMode?: string;
  type?: EntityType;
};

export function FiltersArea({
  resetOffset = noop,
  clearFilter,
  customLabels,
  showStatusFilter = false,
  document_and_ai_search_filter = false,
  exit_document_and_ai_search_filter,
  viewMode,
  type,
}: FilterAreaProps) {
  // "ArrayParam" is how useQueryParams handles things like
  // ?market_segment=abc&market_segment=xyz
  // That URL will become ["abc", "xyz"]
  const history = useHistory();
  const { roleIsSomeKindOfSeller, roleIsGuest } = useAuthContext();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const [pageFilters, setPageFilters] = useState<Filters>();
  const [numericFilters, setNumericFilters] = useState<NumericFilters>();
  const queryObject = () => {
    const querylist: any = {
      active_tag_view: StringParam,
      q: StringParam,
    };
    if (pageFilters) {
      for (const [key] of Object.entries(pageFilters)) {
        querylist[key] = ArrayParam;
      }
    }
    if (numericFilters) {
      for (const [key] of Object.entries(numericFilters)) {
        querylist[key] = ArrayParam;
      }
    }
    return querylist;
  };

  const [query, setQuery] = useQueryParams(queryObject());
  const {
    storefront_id,
    slug: tenantSlug,
    tenant_id,
    collapsed_nav,
  } = useStoreState();
  const { hasPermission } = useAuthContext();
  const canModifyProducts = hasPermission("modify_products");

  const { t } = useTranslation();
  const [cookies] = useCookies([`preferred-language-${tenantSlug}`]);

  const preferredLanguage: SupportedLanguage | undefined = cookies[
    `preferred-language-${tenantSlug}`
  ] as SupportedLanguage;

  function FilterPlaceHolder() {
    return (
      <FiltersWrapper>
        <LoadingIcon width={16} />
      </FiltersWrapper>
    );
  }

  const [filtersFromUrl, setFiltersFromUrl] =
    useState<{ [key: string]: string[] }>();
  const [showPresetFilter, setShowPresetFilter] = useState(false);
  const [filteredItems, setFilteredItems] = useState<IFilters>({});
  const [isUpdatePreset, setIsUpdatePreset] = useState(false);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<IPresetDataItem>();
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const routerLocation = useLocation();
  const theme = useTheme();

  useEffect(() => {
    const filters = loadFilters(routerLocation);
    setFiltersFromUrl(filters);

    const filteredKeys = Object.keys(filters).filter(
      (key) =>
        key !== "perPage" && key !== "offset" && key !== "active_tag_view"
    );
    const hasValidFilters = filteredKeys.some(
      (key) => Array.isArray(filters[key]) && filters[key].length > 0
    );
    setShowPresetFilter(hasValidFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.search, routerLocation.pathname]);

  const { data, error: filtersError } = useSWR<ProductFiltersMainSchema, Error>(
    [
      type
        ? endpoints.v2_storefronts_id_mdm_entity_type_filters(
            storefront_id,
            type
          )
        : endpoints.v2_storefronts_id_products_filters(storefront_id),
      React.useMemo(
        () => ({
          params: document_and_ai_search_filter
            ? { q: query.q }
            : applyFiltersToURL({
                filtersFromUrl,
                params: new URLSearchParams(),
                active_tag_view: query.active_tag_view,
              }),
        }),
        [
          document_and_ai_search_filter,
          filtersFromUrl,
          query.active_tag_view,
          query.q,
        ]
      ),
    ]
  );
  const filters = data?.filters;
  const numericFiltersData = data?.numeric_filters;

  const { data: statusData } = useSWR<ProductStatusesSchema, Error>(
    roleIsSomeKindOfSeller && tenant_id && showStatusFilter
      ? endpoints.v2_tenants_tenant_id_pim_products_status(tenant_id)
      : null
  );
  const statuses = statusData?.statuses;

  const { data: templateData } = useSWR<
    WithPagination<{ data: AttributeTemplateSummarySchema[] }>,
    Error
  >(
    roleIsSomeKindOfSeller &&
      hasPermission("view_templates") &&
      tenant_id &&
      showStatusFilter
      ? `${endpoints.v2_storefronts_id_pim_templates_summary(
          tenant_id
        )}?minimum_number_of_products=1&limit=100`
      : null
  );

  const { data: documentsResponse } = useSWR<
    WithPagination<{ data: Assets[] }>
  >(
    document_and_ai_search_filter
      ? makeUrlWithParams(
          endpoints.v2_tenants_tenant_id_pim_products_document_search(
            tenant_id
          ),
          {
            limit: 15,
            q: query.q,
          }
        )
      : null
  );

  const { data: presetFilterData, mutate: mutatePresetFilter } = useSWR<
    IPresetFilter,
    Error
  >(
    !roleIsGuest
      ? `${
          type
            ? endpoints.v2_storefronts_id_user_filters_type(
                storefront_id,
                type as string
              )
            : endpoints.v2_storefronts_id_user_filters(storefront_id)
        }`
      : null
  );

  useEffect(() => {
    if (presetFilterData?.data) {
      const selectedData = presetFilterData.data.find((item) => item.selected);
      setSelectedPreset(selectedData);
    }
  }, [presetFilterData]);

  const handleFilter = ({
    values,
    filter,
  }: {
    values: ChipType[];
    filter: string;
  }) => {
    const filtersArray = chipsToStringArray(values);
    setQuery({
      [filter]:
        filtersArray.length > 0 && filter === "status"
          ? filtersArray.map((filter) => getProductStatusValue(filter, t))
          : filtersArray.length > 0
          ? filtersArray
          : undefined,
      ...{ active_tag_view: filter },
    });
    if (document_and_ai_search_filter && exit_document_and_ai_search_filter) {
      exit_document_and_ai_search_filter();
    }
    resetOffset(0);
  };

  useEffect(() => {
    if (filters) {
      const filters_by_custom_labels: Filters = {};
      customLabels.forEach(({ filter_type }) => {
        if (filter_type === "status" && !canModifyProducts) {
          return;
        }
        if (filters[filter_type] && filters[filter_type].length > 0) {
          filters_by_custom_labels[filter_type] = filters[filter_type];
        }
      });
      if (statuses && canModifyProducts) {
        filters_by_custom_labels.status = (statuses as string[]).map(
          (item) => ({
            name: getProductStatusText(item as ProductStatusType, t),
            count: 0,
            image_url: null,
            selected: false,
          })
        );
      }
      if (templateData) {
        filters_by_custom_labels.template = templateData.data.map((item) => ({
          id: item.template_id,
          name: item.template_name,
          count: 0,
          image_url: null,
          selected: false,
        }));
      }
      setPageFilters(filters_by_custom_labels);
      setNumericFilters(numericFiltersData);
    }
  }, [
    canModifyProducts,
    customLabels,
    filters,
    numericFiltersData,
    statuses,
    templateData,
    t,
  ]);

  const handleClearFilters = async () => {
    if (selectedPreset) {
      await handleActivePresetId(selectedPreset.id, false);
    }
    setQuery({
      active_tag_view: query.active_tag_view,
    });
    clearFilter();
  };

  const on_filter_item_click = (
    item: FilterGroup & { name: string },
    filter_name: string
  ) => {
    handleClearFilters();
    handleFilter({
      values: [{ name: item.name }],
      filter: filter_name,
    });
  };

  const handleSetFiltersInUrl = React.useCallback(
    (filters: IFilters) => {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, items]) => {
        if (key === "status") {
          items.forEach((item) => {
            params.append(key, getProductStatusValue(item.name, t));
          });
        } else {
          items.forEach((item) => {
            params.append(key, item.name);
          });
        }
      });

      const newUrl = `?${params.toString().replace(/\+/g, "%20")}`;
      history.push({ search: newUrl });
    },
    [history, t]
  );

  useEffect(() => {
    if (!pageFilters || !filtersFromUrl) return;

    const { offset, perPage, ...validFilters } = filtersFromUrl;
    const results: IFilters = {};

    for (const key in validFilters) {
      const filterValues = validFilters[key];
      if (!Array.isArray(filterValues)) continue;
      if (filterValues.length === 1) {
        const criterion = decodeNumericFilterCriterion(filterValues[0]);
        if (criterion) {
          results[key] = [{ id: null, name: filterValues[0], type: "numeric" }];
          continue;
        }
      }

      const mainList = pageFilters[key] || [];
      if (!Array.isArray(mainList)) continue;
      if (key === "status") {
        const matched = mainList
          .filter((item) => {
            return filterValues.some((filterValue: string) => {
              const productStatusText = getProductStatusText(
                filterValue as ProductStatusType,
                t
              );
              return item.name === productStatusText;
            });
          })
          .map((item) => ({
            id: item.id ? String(item.id) : null,
            name: getProductStatusValue(item.name, t),
          }));
        results[key] = matched;
      } else {
        const matched = mainList
          .filter((item) => filterValues.includes(item.name))
          .map((item) => ({
            id: item.id ? String(item.id) : null,
            name: item.name,
          }));
        results[key] = matched;
      }
    }

    setFilteredItems(results);
  }, [pageFilters, filtersFromUrl, t]);

  const getNormalizedFilterItems = (filter: IFilters): string[] => {
    return Object.values(filter)
      .reduce((acc, arr) => acc.concat(arr), [])
      .map((item) => item.name.toLocaleLowerCase());
  };

  const areArraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    return a.every((value, index) => value === b[index]);
  };

  useEffect(() => {
    const filter = presetFilterData?.data?.[0]?.filters;

    if (!filter) {
      setIsUpdatePreset(true);
      return;
    }

    if (filter && filteredItems) {
      const ids = getNormalizedFilterItems(filter);
      const cleanFilteredItems = getNormalizedFilterItems(filteredItems);

      const result = areArraysEqual(cleanFilteredItems, ids);
      setIsUpdatePreset(result);
    }
  }, [filteredItems, presetFilterData]);

  useEffect(() => {
    const filters = presetFilterData?.data?.[0]?.filters;
    const hasPresetSelected = presetFilterData?.data?.[0]?.selected;

    if (hasPresetSelected && filters) {
      handleSetFiltersInUrl(filters);
    }
  }, [handleSetFiltersInUrl, presetFilterData, viewMode]);

  const shouldShowPresetFilter =
    (showPresetFilter || (presetFilterData?.data?.length ?? 0) > 0) &&
    !roleIsGuest;

  const handleActivePresetId = async (
    presetId: string,
    isPresetSelected: boolean
  ) => {
    try {
      await axios.patch(
        type
          ? endpoints.v2_storefronts_id_user_filters_type_userFilterId(
              storefront_id,
              type,
              presetId
            )
          : endpoints.v2_storefronts_id_user_filters_userFilterId(
              storefront_id,
              presetId
            ),
        {
          selected: isPresetSelected,
        }
      );

      await mutatePresetFilter();
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleSubmitPreset = async (preset_name: string) => {
    setShowPresetDialog(false);
    const presetId = presetFilterData?.data?.[0]?.id || "";
    try {
      const request = isEditMode
        ? axios.patch(
            type
              ? endpoints.v2_storefronts_id_user_filters_type_userFilterId(
                  storefront_id,
                  type,
                  presetId
                )
              : endpoints.v2_storefronts_id_user_filters_userFilterId(
                  storefront_id,
                  presetId
                ),
            { name: preset_name }
          )
        : axios.post(
            type
              ? endpoints.v2_storefronts_id_user_filters_type(
                  storefront_id,
                  type
                )
              : endpoints.v2_storefronts_id_user_filters(storefront_id),
            {
              name: preset_name,
              filters: filteredItems,
              selected: true,
            }
          );

      const response = await request;

      if (response.status === 201) {
        setIsEditMode(false);
        mutatePresetFilter();
        notifySuccess(
          t(isEditMode ? "Preset updated" : "New Preset saved") as string
        );
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleDeletePreset = async (id: string) => {
    try {
      const response = await axios.delete(
        type
          ? endpoints.v2_storefronts_id_user_filters_type_userFilterId(
              storefront_id,
              type,
              id
            )
          : endpoints.v2_storefronts_id_user_filters_userFilterId(
              storefront_id,
              id
            )
      );
      if (response.status === 204) {
        mutatePresetFilter();
        notifySuccess(t("Preset deleted") as string);
        clearFilter();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleUpdatePreset = async () => {
    const presetId = selectedPreset?.id || "";
    try {
      const response = await axios.patch(
        endpoints.v2_storefronts_id_user_filters_userFilterId(
          storefront_id,
          presetId
        ),
        {
          filters: filteredItems,
        }
      );
      if (response.status === 201) {
        mutatePresetFilter();
        notifySuccess(t("Preset updated") as string);
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const handleApplyPreset = async (id: string) => {
    const filters = presetFilterData?.data?.[0]?.filters;
    if (filters) {
      await handleActivePresetId(id, true);
      handleSetFiltersInUrl(filters);
    }
  };

  const calculateVisibleButtons = () => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const buttonEls = container.querySelectorAll(".button-item");
    let count = 0;
    const maxHeight = container.clientHeight;
    const containerTop = container.getBoundingClientRect().top;

    for (let btn of buttonEls) {
      const { bottom } = btn.getBoundingClientRect();
      if (bottom - containerTop <= maxHeight) {
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count);
    setShouldShowToggle(buttonEls.length > count);
  };

  useEffect(() => {
    calculateVisibleButtons();

    const handleResize = () => calculateVisibleButtons();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [expanded]);

  useEffect(() => {
    const timeout_id = setTimeout(() => {
      calculateVisibleButtons();
    }, 300);
    return () => {
      clearTimeout(timeout_id);
    };
  }, [pageFilters, collapsed_nav]);

  // Could potentially display an error notification after multiple failed
  // attempts. That is not yet set up in this project.
  if (!filters && !filtersError) {
    // stop layout from shifting on page load.
    return <FilterPlaceHolder />;
  } else if (filtersError) {
    // Search still works if filters don't load.
    // This maintains the layout of the page in case of error.
    return <EmptyFiltersPlaceholder />;
  } else if (filters) {
    // Handle case that happened once on staging where all filters were empty arrays.
    // This might theoretically be possible if a tenant is activated and no filters
    // are added.
    const pageFiltersCount = Object.keys(pageFilters ?? {}).length;
    const totalFilterCount =
      pageFiltersCount + Object.keys(numericFilters ?? {}).length;
    return (!!pageFilters && Object.entries(pageFilters).length > 0) ||
      (!!numericFilters && Object.entries(numericFilters).length > 0) ||
      (!!documentsResponse && documentsResponse.data.length > 0) ? (
      <>
        {document_and_ai_search_filter ? (
          <SearchFilterWrapper>
            {!!documentsResponse && documentsResponse.data.length > 0 && (
              <SearchFilterItem>
                <FilterNameWrapper>{t("Documents")}</FilterNameWrapper>
                <SearchFilterTags>
                  <DocumentFilterTag documents={documentsResponse.data} />
                </SearchFilterTags>
              </SearchFilterItem>
            )}
            {!!pageFilters &&
              Object.entries(pageFilters).map(([filter_name, filter_items]) => (
                <SearchFilterItem key={filter_name}>
                  <FilterNameWrapper>
                    {getCustomLabel({
                      filter_type: filter_name,
                      tag_classification_configs: customLabels,
                      preferred_language: preferredLanguage,
                    })}
                  </FilterNameWrapper>
                  <SearchFilterTags>
                    <SearchFilterTag
                      filter_items={filter_items}
                      on_filter_item_click={(item) =>
                        on_filter_item_click(item, filter_name)
                      }
                    />
                  </SearchFilterTags>
                </SearchFilterItem>
              ))}
          </SearchFilterWrapper>
        ) : (
          <>
            {shouldShowPresetFilter && (
              <PresetFilterWrapper>
                <PresetFilter
                  presetFilterData={presetFilterData}
                  isUpdatePreset={isUpdatePreset}
                  selectedPreset={selectedPreset}
                  showPresetDialog={showPresetDialog}
                  setShowPresetDialog={setShowPresetDialog}
                  setIsEditMode={setIsEditMode}
                  onSubmitPreset={handleSubmitPreset}
                  onDeletePreset={handleDeletePreset}
                  onApplyPreset={handleApplyPreset}
                  onUpdatePreset={handleUpdatePreset}
                />
              </PresetFilterWrapper>
            )}

            <FilterPortalContainer id="filter-dropdown-portal" />

            <FiltersWrapper
              ref={containerRef}
              style={{
                maxHeight: expanded ? "none" : "calc(3 * 41px)",
                overflow: expanded ? "visible" : "hidden",
                transition: "max-height 0.3s ease",
                gap: theme.resolve("spacing_xs"),
              }}
            >
              {!!pageFilters &&
                Object.entries(pageFilters).map(
                  ([filter_name, filter_items], idx) => {
                    const restCount = totalFilterCount - visibleCount + 1;
                    const isLastVisible = !expanded && idx === visibleCount - 1;

                    return (
                      <FilterByWrapper key={filter_name}>
                        <FilterBy
                          setter={handleFilter}
                          options={filter_items}
                          filter={filter_name}
                          header={getCustomLabel({
                            filter_type: filter_name,
                            tag_classification_configs: customLabels,
                            preferred_language: preferredLanguage,
                          })}
                          label={getCustomLabel({
                            filter_type: filter_name,
                            tag_classification_configs: customLabels,
                            preferred_language: preferredLanguage,
                          })}
                          preselectedFilters={
                            convertToChipArray(
                              filtersFromUrl && filter_name === "status"
                                ? filtersFromUrl[filter_name]?.map((option) =>
                                    getProductStatusText(
                                      option as ProductStatusType,
                                      t
                                    )
                                  )
                                : filtersFromUrl
                                ? filtersFromUrl[filter_name]
                                : []
                            ) ?? []
                          }
                          isLastVisible={shouldShowToggle && isLastVisible}
                          usePortal={true}
                          size="small"
                        />

                        {shouldShowToggle && (
                          <TertiaryTextButtonSmall
                            onClick={() => setExpanded(!expanded)}
                            style={{
                              position: "absolute",
                              top: "11px",
                              left: "0",
                              display: isLastVisible ? "flex" : "none",
                            }}
                          >
                            {!expanded && `+ ${restCount} more`}
                          </TertiaryTextButtonSmall>
                        )}
                      </FilterByWrapper>
                    );
                  }
                )}
              {!!numericFilters &&
                Object.entries(numericFilters).map(
                  ([filter_name, meta], numericIdx) => {
                    const idx = pageFiltersCount + numericIdx;
                    const restCount = totalFilterCount - visibleCount + 1;
                    const isLastVisible = !expanded && idx === visibleCount - 1;

                    return (
                      <FilterByWrapper key={filter_name}>
                        <NumericFilterButton
                          filterType={filter_name}
                          label={getCustomLabel({
                            filter_type: filter_name,
                            tag_classification_configs: customLabels,
                            preferred_language: preferredLanguage,
                          })}
                          operators={meta.operators}
                          preselectedFilters={
                            convertToChipArray(
                              filtersFromUrl ? filtersFromUrl[filter_name] : []
                            ) ?? []
                          }
                          setter={handleFilter}
                          className="button-item"
                          isLastVisible={shouldShowToggle && isLastVisible}
                          usePortal={true}
                        />

                        {shouldShowToggle && (
                          <TertiaryTextButtonSmall
                            onClick={() => setExpanded(!expanded)}
                            style={{
                              position: "absolute",
                              top: "11px",
                              left: "0",
                              display: isLastVisible ? "flex" : "none",
                            }}
                          >
                            {!expanded && `+ ${restCount} more`}
                          </TertiaryTextButtonSmall>
                        )}
                      </FilterByWrapper>
                    );
                  }
                )}

              {filtersFromUrl && Object.entries(filtersFromUrl)?.length > 0 && (
                <TertiaryTextButtonSmall
                  onClick={() => {
                    handleClearFilters();
                  }}
                  style={{ marginBottom: 0 }}
                >
                  {t("Clear Filters")}
                </TertiaryTextButtonSmall>
              )}
              {expanded && (
                <TertiaryTextButtonSmall
                  onClick={() => setExpanded(!expanded)}
                  style={{ marginBottom: 0 }}
                >
                  {t("Collapse")}
                </TertiaryTextButtonSmall>
              )}
            </FiltersWrapper>
          </>
        )}
      </>
    ) : (
      <></>
    );
  } else return null;
}
